import { useCallback, useEffect, useRef, useState } from 'react';
import { eventsApi } from '../../../api/eventsApi';
import { formatElapsedSince } from '../../../utils/format';
import { isRunningStatus } from '../../../constants/jobStatus';

const POLL_MS = 3000;

/**
 * Status of the event statistics report. Polls while a run is in progress;
 * `revision` increases each time a run completes so the display can reload.
 */
export function useEventReportStatus(eventId) {
  const [status, setStatus] = useState(null);
  const [revision, setRevision] = useState(0);
  const [elapsed, setElapsed] = useState(null);
  const timer = useRef(null);
  const wasRunning = useRef(false);

  const poll = useCallback(async () => {
    window.clearTimeout(timer.current);
    let next = null;
    try {
      next = await eventsApi.getReportStatus(eventId);
    } catch {
      return;
    }
    setStatus(next);
    const nowRunning = isRunningStatus(next?.status);
    if (nowRunning) {
      timer.current = window.setTimeout(poll, POLL_MS);
    } else if (wasRunning.current && next?.status === 'COMPLETED') {
      setRevision((r) => r + 1);
    }
    wasRunning.current = nowRunning;
  }, [eventId]);

  useEffect(() => {
    poll();
    return () => window.clearTimeout(timer.current);
  }, [poll]);

  const running = isRunningStatus(status?.status);

  useEffect(() => {
    if (!running) {
      setElapsed(null);
      return undefined;
    }
    const since = new Date(status.queued_at || status.processing_started_at || Date.now()).getTime();
    const tick = () => setElapsed(formatElapsedSince(since));
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [running, status?.queued_at, status?.processing_started_at]);

  return { status, running, elapsed, revision, refresh: poll };
}
